import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isOfferFailed, LouiseOffer } from '../../../../../types/Quotes';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import OfferFailure from './OfferFailure';
import SingleOfferInfoContainer from './SingleOfferInfoContainer';
import SingleOfferSummaryContainer from './SingleOfferSummaryContainer';
import SingleOfferWrapper from './SingleOfferWrapper';

import SingleOfferError from './SingleOfferError';
import { useIsFetching } from 'react-query';
import OfferParametersPopover from './offers-discount-modal/OfferParametersPopover';
import { OfferState } from '../../services/offersService';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import useIsNewRoute from '../../features/offers/hooks/useIsNewRoute';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  offer: LouiseOffer;
  isComparing?: boolean;
  isSelected?: boolean;
  showErrorModal: boolean;
  closeErrorModal: () => void;
  onAddToSelected: () => void;
  refetch: () => void;
  onReport: () => void;
  onOptionsChange: (arg0: string[]) => void;
}

const SingleOfferContainer: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  isComparing,
  isSelected,
  showErrorModal,
  closeErrorModal,
  onAddToSelected,
  refetch,
  onReport,
  onOptionsChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isNewRoute = useIsNewRoute();

  const sessionId = useSessionId();
  const isFetchingQuote = useIsFetching([
    sessionId,
    'quotes',
    offer.info.insurance_type.toLowerCase(),
    offer.hash_id,
  ]);

  return (
    <OuterWrapper data-test-id="ADVISORY-CONVO_OFFERS_SINGLE-OFFER_WRAPPER">
      {!isSocialLoginUrl && (
        <OfferParametersPopover
          offer={offer}
          disabled={offer.state === OfferState.LOADING}
        />
      )}
      <SingleOfferWrapper isSelected={isSelected}>
        {showErrorModal && (
          <OfferFailure
            message={t('There was a problem calculating this offer')}
            onTryAgain={refetch}
            closeErrorModal={closeErrorModal}
            onReport={onReport}
            isFetching={!!isFetchingQuote}
          />
        )}

        <SingleOfferInfoContainer
          offer={offer}
          isOpen={isOpen}
          onOptionsChange={onOptionsChange}
        />

        {isOfferFailed(offer) ? (
          <SingleOfferError
            offer={offer}
            refetch={refetch}
            onReport={onReport}
            isFetching={!!isFetchingQuote}
            showErrorModal={showErrorModal}
          />
        ) : (
          <SingleOfferSummaryContainer
            offer={offer}
            isComparing={isComparing}
            onAddToSelected={onAddToSelected}
            isOpen={isOpen}
            isSelected={isSelected}
          />
        )}
      </SingleOfferWrapper>
      {!['two_wheeler'].includes(offer.info.insurance_type) && !isNewRoute && (
        <ExtendButton
          withBorder={!showErrorModal}
          onClick={() =>
            [OfferState.FETCHED, OfferState.ERROR].includes(offer.state) &&
            setIsOpen((prev) => !prev)
          }
        >
          {isOpen ? t('Less information') : t('More information')}
        </ExtendButton>
      )}
    </OuterWrapper>
  );
};

const OuterWrapper = styled.div`
  position: relative;
  display: none;

  ${mq.laptop} {
    display: block;
  }
`;

const ExtendButton = styled.button<{ withBorder: boolean }>`
  z-index: 2;
  color: ${({ theme }) => theme.labels.guaranteeText};
  padding: 0.8rem;
  width: 100%;
  max-width: 22.4rem;
  border-radius: 50px;
  background-color: white;
  border: ${({ theme, withBorder }) =>
    withBorder && `1px solid ${theme.gray[50]}`};
  outline: none;
  cursor: ${({ withBorder }) => (withBorder ? 'pointer' : 'unset')};
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 2px rgba(40, 41, 61, 0.04), 0 4px 8px rgba(96, 97, 112, 0.16);

  transition: 0.2s color;
  &:hover {
    color: ${({ theme, withBorder }) => withBorder && theme.brand.primary};
  }
`;

export default SingleOfferContainer;
